import { type JSX } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { DashboardModal } from '@/shared/components/dashboards/Modal';
import Button from '@/shared/components/flip-ui-kit/Button';
import CloseIcon from '@/shared/icons/flip-ui-kit/large/Close';
import CheckCircleIcon from '@/shared/icons/flip-ui-kit/small/CheckCircle';
import CircleIcon from '@/shared/icons/flip-ui-kit/small/Circle';
import useOffboarding from '../../hooks/useOffboarding';

const ChecklistItem = ({
  title,
  subtitle,
  completed,
}: {
  title: string;
  subtitle: string;
  completed: boolean;
}) => (
  <div className="flex flex-col gap-y-2 text-14 font-semibold text-cf-light-2">
    <div className="flex items-center gap-x-2">
      {completed ? <CheckCircleIcon className="text-cf-green-1" /> : <CircleIcon />}
      <span className="text-cf-white">{title}</span>
    </div>
    <div className={classNames(completed && 'line-through')}>{subtitle}</div>
  </div>
);

export const OffboardModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element | null => {
  const offboard = useOffboarding();
  const router = useRouter();

  return (
    <DashboardModal open={open} onClose={onClose}>
      <div className="flex w-[429px] flex-col items-center justify-center gap-6 rounded-md border border-cf-gray-4 bg-cf-gray-3 p-6">
        <div className="flex w-full items-start justify-between">
          <div className="flex flex-1 flex-col space-y-2">
            <span className="font-aeonikMedium text-24 text-cf-white">Close account</span>
            <div className="text-14 text-cf-light-2">
              You must complete the following steps before you can close your account.
            </div>
          </div>
          <button type="button" onClick={onClose} className="outline-cf-gray-5">
            <CloseIcon />
          </button>
        </div>
        <div className="h-px w-full bg-cf-gray-4" />
        <div className="flex w-full flex-col gap-y-4">
          <ChecklistItem
            title="Close all orders"
            subtitle="Go to Orders and make sure all orders are closed"
            completed={!offboard.hasOrders}
          />
          <ChecklistItem
            title="Stop boosting"
            subtitle="Go to Boost and remove all liquidity"
            completed={!offboard.hasBoostPositions}
          />
          <ChecklistItem
            title="Withdraw all assets"
            subtitle="Go to Balances and withdraw all funds after closing orders and stopping boosting"
            completed={!offboard.hasBalances && !offboard.hasOrders && !offboard.hasBoostPositions}
          />
        </div>
        <div className="h-px w-full bg-cf-gray-4" />
        <div className="flex gap-x-4 self-end">
          <Button type="secondary-standard" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="primary-standard"
            disabled={offboard.step === 'checklist' || offboard.step === 'unknown'}
            onClick={() => router.push('/offboard')}
            loading={offboard.step === 'unknown'}
          >
            Begin offboarding
          </Button>
        </div>
      </div>
    </DashboardModal>
  );
};
